import { useEffect, useMemo, useState } from "react";
import { PageHeader, SectionDivider } from "@/components/primitives";
import { api } from "@/lib/api";
import { ChevronRight, BookOpen } from "lucide-react";

export default function Codex() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/codex").then((r) => setEntries(r.data)).catch(() => {}); }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (!m.has(e.section_label)) m.set(e.section_label, []);
      m.get(e.section_label).push(e);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.order - b.order);
    return [...m.entries()];
  }, [entries]);

  return (
    <div data-testid="codex-page">
      <PageHeader
        kicker="The Keeper's Codex"
        title="The Codex"
        subtitle="A small manual of dahlia keeping, written by hand"
      />

      {grouped.length === 0 && (
        <p className="text-center font-display italic text-ink-soft py-12">Opening the codex...</p>
      )}

      {grouped.map(([label, items]) => (
        <section key={label} className="mt-6" data-testid={`codex-section-${slug(label)}`}>
          <SectionDivider label={label} />
          <div className="mt-4 space-y-3">
            {items.map((e) => {
              const id = `${slug(label)}-${e.order}`;
              const isOpen = open === id;
              return (
                <article
                  key={id}
                  className="h-card overflow-hidden"
                  data-testid={`codex-entry-${id}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : id)}
                    className="w-full flex items-center gap-4 p-4 text-left hover:bg-cream-deep/30 transition-colors"
                    data-testid={`codex-toggle-${id}`}
                  >
                    <span className="w-11 h-11 rounded-full bg-cream-deep text-sage-dark flex items-center justify-center shrink-0">
                      <BookOpen size={18} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <div className="kicker text-[10px]">{label}</div>
                      <div className="font-display text-xl sm:text-2xl text-sage-dark leading-tight">{e.title}</div>
                      {e.intro && (
                        <div className="font-display italic text-ink-soft text-sm sm:text-base">{e.intro}</div>
                      )}
                    </span>
                    <ChevronRight
                      size={18}
                      className={`text-sage-dark shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div
                      className="px-5 pb-5 -mt-1 animate-fade-up"
                      data-testid={`codex-body-${id}`}
                    >
                      <div className="divider mb-3"><span>· read on ·</span></div>
                      <p className="font-display text-lg sm:text-xl text-ink leading-relaxed whitespace-pre-line">
                        {e.body}
                      </p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}

      <p className="text-center font-display italic text-ink-soft mt-10">
        Every tuber, every cutting, every length of advice — earned from soil we know by hand.
      </p>
    </div>
  );
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
